/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { Globe, RefreshCw, Eye, Clock, ExternalLink, Compass, Repeat, Radio } from "lucide-react";
import { adminAudienceReach } from "../../lib/api";

/**
 * ReachPanel — admin-only editorial reach + engagement signal.
 *
 * Privacy contract:
 *   - Never displays IPs, exact locations, cities, or GPS.
 *   - Country codes are coarse ISO-2 only (CF-IPCountry / Vercel header).
 *     If no upstream header is present, country panel is hidden.
 *   - Admins are already excluded server-side at ingestion.
 *   - This panel is read-only and aggregate-only.
 */

const SECONDS_TO_HUMAN = (n) => {
  const s = Math.max(0, Math.round(Number(n) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const Tile = ({ icon: Icon, label, value, sub = null, testid }) => (
  <div
    data-testid={testid}
    className="border border-[#2D2A26] bg-[#0F0F0F] px-4 py-3"
  >
    <div className="flex items-center justify-between">
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">{label}</div>
      {Icon ? <Icon size={12} className="text-[#D92D20]/70" /> : null}
    </div>
    <div className="font-display text-2xl text-[#E4DFD3] mt-1 tabular-nums">{value ?? "—"}</div>
    {sub != null && (
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] mt-0.5">{sub}</div>
    )}
  </div>
);

const Bar = ({ value, max, color = "#D92D20" }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value * 100) / max)) : 0;
  return (
    <div className="h-1 bg-[#1a1a1a] mt-1 relative">
      <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.7 }} />
    </div>
  );
};

const cleanRouteLabel = (route) => {
  if (!route) return "/";
  // No PII to scrub here — routes are static topic/entity paths — but keep
  // the display compact.
  if (route.length > 60) return `${route.slice(0, 57)}…`;
  return route;
};

const SectionHeader = ({ icon: Icon, children, count }) => (
  <div className="flex items-baseline justify-between mb-3">
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
      {Icon ? <Icon size={12} /> : null} // {children}
    </div>
    {count !== undefined && (
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{count}</span>
    )}
  </div>
);

const COUNTRY_NAMES = (() => {
  try {
    if (typeof Intl !== "undefined" && Intl.DisplayNames) {
      const dn = new Intl.DisplayNames(["en"], { type: "region" });
      return (code) => {
        try { return dn.of(code) || code; } catch { return code; }
      };
    }
  } catch { /* noop */ }
  return (code) => code;
})();

export const ReachPanel = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [error, setError] = useState(null);

  const load = useCallback(async (d = days) => {
    setLoading(true); setError(null);
    try {
      const res = await adminAudienceReach(token, d, userToken);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load reach.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, userToken, days]);

  useEffect(() => { if (token || userToken) load(days); }, [token, userToken, days, load]);

  const totals = data?.totals || {};
  const countries = data?.countries || [];
  const topPages = data?.top_pages || [];
  const topRead = data?.top_dossiers_by_read_time || [];
  const topSrc = data?.top_dossiers_by_source_clicks || [];
  const geoSupported = !!data?.geo_supported;

  const maxCountry = countries[0]?.count || 0;
  const maxPage = topPages[0]?.views || 0;
  const maxRead = topRead[0]?.seconds || 0;
  const maxSrc = topSrc[0]?.source_clicks || 0;

  return (
    <section data-testid="reach-panel" className="mt-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Compass size={14} /> // REACH — EDITORIAL SIGNAL
          </div>
          <h2 className="font-display text-3xl text-[#E4DFD3]">How far is The Archive reaching?</h2>
          <p className="text-[#A39E93] text-sm mt-1 max-w-2xl">
            Coarse, anonymised reach and engagement. No IPs, no cities, no GPS. Country codes
            are pulled from upstream proxy headers only — when unavailable, the country panel
            is hidden rather than guessed. Admin sessions are excluded from these aggregates.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="reach-days-select"
            className="bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2"
          >
            <option value={1}>last 24h</option>
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
          </select>
          <button
            onClick={() => load(days)}
            data-testid="reach-refresh-btn"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-[#D92D20]/40 bg-[#1a0808] text-[#D92D20] px-4 py-3 mb-4 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="reach-error">
          // {error}
        </div>
      )}

      {/* Top-line tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Tile testid="reach-tile-today"  icon={Eye}    label="Visitors today" value={totals.visitors_today ?? 0} />
        <Tile testid="reach-tile-window" icon={Eye}    label={`Visitors (${days}d)`} value={totals.visitors_window ?? 0} />
        <Tile testid="reach-tile-live"   icon={Radio}  label="Live (excl. admin)" value={totals.live_now ?? 0} sub="last 2 min" />
        <Tile testid="reach-tile-return" icon={Repeat} label="Returning" value={totals.returning_visitors ?? 0} sub="≥30m gap" />
        <Tile testid="reach-tile-countries" icon={Globe} label="Countries" value={geoSupported ? countries.length : "—"} sub={geoSupported ? undefined : "geo header missing"} />
      </div>

      {/* Two-col grid: countries + top pages */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4" data-testid="reach-section-countries">
          <SectionHeader icon={Globe} count={geoSupported ? countries.length : 0}>
            COUNTRIES / REGIONS
          </SectionHeader>
          {!geoSupported ? (
            <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.25em] italic">
              Country data not available — upstream proxy is not providing a privacy-safe
              country header (CF-IPCountry / X-Vercel-IP-Country). No IPs are stored.
            </div>
          ) : countries.length === 0 ? (
            <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.25em] italic">
              No country data in this window.
            </div>
          ) : (
            countries.slice(0, 24).map((c) => (
              <div key={c.country} className="flex items-center gap-3 mb-1.5" data-testid={`reach-country-${c.country}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] w-7">{c.country}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#E4DFD3] truncate">{COUNTRY_NAMES(c.country)}</div>
                  <Bar value={c.count} max={maxCountry} />
                </div>
                <div className="font-mono text-xs text-[#CBB892] tabular-nums w-10 text-right">{c.count}</div>
              </div>
            ))
          )}
        </div>

        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4" data-testid="reach-section-pages">
          <SectionHeader icon={Eye} count={topPages.length}>
            TOP PUBLIC PAGES · VIEWS
          </SectionHeader>
          {topPages.length === 0 ? (
            <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.25em] italic">
              No public page views in this window.
            </div>
          ) : (
            topPages.slice(0, 14).map((p, i) => (
              <div key={p.route || i} className="flex items-center gap-3 mb-1.5" data-testid={`reach-page-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <a
                    href={p.route}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#E4DFD3] truncate hover:text-[#D92D20] inline-flex items-center gap-1"
                  >
                    {cleanRouteLabel(p.route)} <ExternalLink size={9} className="text-[#757065] shrink-0" />
                  </a>
                  <Bar value={p.views} max={maxPage} />
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                    {p.unique_sessions} unique
                  </div>
                </div>
                <div className="font-mono text-xs text-[#CBB892] tabular-nums w-12 text-right">{p.views}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Read time + source clicks */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4" data-testid="reach-section-read">
          <SectionHeader icon={Clock} count={topRead.length}>
            TOP DOSSIERS · READ TIME
          </SectionHeader>
          {topRead.length === 0 ? (
            <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.25em] italic">
              No read-time recorded yet.
            </div>
          ) : (
            topRead.slice(0, 14).map((r, i) => (
              <div key={r.dossier_slug || i} className="flex items-center gap-3 mb-1.5" data-testid={`reach-read-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <a
                    href={`/topic/${(r.dossier_slug || "").replace(/^epstein:/, "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#E4DFD3] truncate hover:text-[#D92D20] inline-flex items-center gap-1"
                  >
                    {r.title || r.dossier_slug} <ExternalLink size={9} className="text-[#757065] shrink-0" />
                  </a>
                  <Bar value={r.seconds} max={maxRead} color="#CBB892" />
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                    {r.unique_sessions} unique
                  </div>
                </div>
                <div className="font-mono text-xs text-[#CBB892] tabular-nums w-16 text-right">
                  {SECONDS_TO_HUMAN(r.seconds)}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4" data-testid="reach-section-sources">
          <SectionHeader icon={ExternalLink} count={topSrc.length}>
            TOP DOSSIERS · SOURCE CLICKS
          </SectionHeader>
          {topSrc.length === 0 ? (
            <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.25em] italic">
              No outbound source clicks tied to a dossier yet.
            </div>
          ) : (
            topSrc.slice(0, 14).map((r, i) => (
              <div key={r.dossier_slug || i} className="flex items-center gap-3 mb-1.5" data-testid={`reach-src-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <a
                    href={`/topic/${(r.dossier_slug || "").replace(/^epstein:/, "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#E4DFD3] truncate hover:text-[#D92D20] inline-flex items-center gap-1"
                  >
                    {r.title || r.dossier_slug} <ExternalLink size={9} className="text-[#757065] shrink-0" />
                  </a>
                  <Bar value={r.source_clicks} max={maxSrc} color="#5DD4B3" />
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                    {r.unique_sessions} unique
                  </div>
                </div>
                <div className="font-mono text-xs text-[#CBB892] tabular-nums w-12 text-right">{r.source_clicks}</div>
              </div>
            ))
          )}
        </div>
      </div>

      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mt-4">
        // no IPs · no exact locations · admin sessions excluded · 35-day retention
      </p>
    </section>
  );
};

export default ReachPanel;
